import { useCallback } from 'react';
import countryList from 'react-select-country-list';
import { useAdminCollections } from '../../../../../context/AdminCollections';
import { useUserPermissions } from '../../../../hooks/useUserPermissions';
import {
  CMSE_COMMITTEE_IDS,
  CM_PRIMARY_ID,
  ELECTION_TYPE,
} from '../../../../../utils/constants';

const useCommitteeHelpers = () => {
  const { committeeArray } = useAdminCollections();
  const { isSCChair, userCommittees } = useUserPermissions();

  const getCountryName = useCallback((countryCode) => {
    if (!countryCode) return '';
    const label = countryList().getLabel(countryCode);
    return label || countryCode;
  }, []);

  const isCorrespondingMembership = useCallback(
    (committee) =>
      committee.electionTypeId === ELECTION_TYPE.CORRESPONDING_MEMBER,
    [],
  );

  const formatCandidate = useCallback(
    (candidate, electionTypeId) => {
      const isCorresponding =
        electionTypeId === ELECTION_TYPE.CORRESPONDING_MEMBER;
      return {
        rank: candidate.rank,
        name: candidate.candidateName,
        gender: candidate.gender || 'N/A',
        country: getCountryName(candidate.countryOfBirth) || 'N/A',
        institution: candidate.institution || 'N/A',
        stateOrCountry: isCorresponding
          ? candidate.addressCountry || 'N/A'
          : candidate.stateTerritory || 'N/A',
        expertise: candidate.fieldOfExpertise || 'N/A',
      };
    },
    [getCountryName],
  );

  const getCommitteeInfo = useCallback(
    (committee) => {
      const rawId = Number(committee.sectionalCommitteeId || committee.id);
      const committeeIdToMatch = CMSE_COMMITTEE_IDS.includes(rawId)
        ? CM_PRIMARY_ID
        : rawId;

      const matched = committeeArray?.find(
        (c) => Number(c.id) === Number(committeeIdToMatch),
      );

      const chairName =
        matched?.attributes?.committeeChair?.data?.attributes?.username?.split(
          ' (',
        )[0] || 'Not assigned';
      const observers =
        matched?.attributes?.councilObservers?.data
          ?.map((obs) => obs.attributes?.username?.split(' (')[0])
          .join(', ') || 'None';

      return { chairName, observers };
    },
    [committeeArray],
  );

  const getCommitteeMembers = useCallback(
    (committee) => {
      const rawId = Number(committee.sectionalCommitteeId || committee.id);
      const committeeIdToMatch = CMSE_COMMITTEE_IDS.includes(rawId)
        ? CM_PRIMARY_ID
        : rawId;

      const matched = committeeArray?.find(
        (c) => Number(c.id) === Number(committeeIdToMatch),
      );

      if (!matched) return [];

      const members = [];
      const chair = matched.attributes?.committeeChair?.data;

      const committeeMembers = matched.attributes?.committeeMembers?.data || [];
      committeeMembers.forEach((member) => {
        if (!chair || member.id !== chair.id) {
          members.push({
            id: member.id,
            name: member.attributes?.username?.split(' (')[0] || 'Unknown',
            role: 'Member',
          });
        }
      });

      return members;
    },
    [committeeArray],
  );

  const isChairOfCommittee = useCallback(
    (committee) => {
      if (!isSCChair) return false;
      return userCommittees?.some((uc) => {
        const ucId = Number(uc.id);
        const committeeId = Number(
          committee.sectionalCommitteeId || committee.id,
        );
        if (CMSE_COMMITTEE_IDS.includes(committeeId)) {
          return CMSE_COMMITTEE_IDS.includes(ucId);
        }
        return ucId === committeeId;
      });
    },
    [isSCChair, userCommittees],
  );

  const getLastColumnLabel = useCallback(
    (committee) =>
      isCorrespondingMembership(committee) ? 'Country' : 'State or Territory',
    [isCorrespondingMembership],
  );

  return {
    getCountryName,
    isCorrespondingMembership,
    formatCandidate,
    getCommitteeInfo,
    getCommitteeMembers,
    isChairOfCommittee,
    getLastColumnLabel,
  };
};

export default useCommitteeHelpers;
